import {
  CreateParams,
  CreateResult,
  DeleteParams,
  DeleteResult,
  GetListParams,
  GetListResult,
  GetManyReferenceParams,
  GetManyReferenceResult,
  UpdateParams,
  UpdateResult,
  GetOneParams,
  GetOneResult,
} from "react-admin";
import { applyFilter, applyPagination, applySort, splitCompositeId } from "../backoffice.utils";
import { AbstractDataProvider } from "./AbstractDataProvider";
import { OtherEventCategoryDto, schemas, serverAPI } from "../api/server.api";

export type DrivingSchoolOtherEventCategory = OtherEventCategoryDto;

class DrivingSchoolOtherEventCategoriesProvider extends AbstractDataProvider<DrivingSchoolOtherEventCategory> {
  async getOne(
    _resource: string,
    { id }: GetOneParams<DrivingSchoolOtherEventCategory>,
  ): Promise<GetOneResult<DrivingSchoolOtherEventCategory>> {
    const [drivingSchoolId, otherEventCategoryId] = splitCompositeId(id);
    const otherEventCategory = await serverAPI.getOtherEventCategory({ id: otherEventCategoryId, drivingSchoolId });
    return { data: _toRecord(otherEventCategory) };
  }

  async getList(
    _resource: string,
    { filter, sort, pagination }: GetListParams,
  ): Promise<GetListResult<DrivingSchoolOtherEventCategory>> {
    const { drivingSchoolId, ...clientSideFilter } = filter ?? {};
    if (!drivingSchoolId) {
      throw new Error(`Unexpected filter: ${JSON.stringify(filter)} -- expected: {"drivingSchoolId": ...}`);
    }
    const otherEventCategories = await serverAPI.getOtherEventCategories({ drivingSchoolId });
    return applyPagination(
      applySort(applyFilter(otherEventCategories.map(_toRecord), clientSideFilter), sort),
      pagination,
    );
  }

  async getManyReference(
    _resource: string,
    { target, id, filter, sort, pagination }: GetManyReferenceParams,
  ): Promise<GetManyReferenceResult<DrivingSchoolOtherEventCategory>> {
    if (target !== "drivingSchoolId") {
      throw new Error(`Invalid target: ${JSON.stringify(target)} -- expected: "drivingSchoolId"`);
    }
    return this.getList("otherEventCategories", { filter: { drivingSchoolId: id, ...filter }, sort, pagination });
  }

  async create(
    _resource: string,
    { data, meta }: CreateParams<DrivingSchoolOtherEventCategory>,
  ): Promise<CreateResult<DrivingSchoolOtherEventCategory>> {
    if (!meta || !meta.drivingSchoolId) {
      throw new Error(`Invalid meta: ${JSON.stringify(meta)} -- expected: "drivingSchoolId"`);
    }
    const payload = schemas.CreateOtherEventCategoryDto.parse({
      ...data,
      availableDurations: [],
      isRestTime: false,
      isWorkTime: data.isPaid ?? false,
      workedHoursCategory: "other",
      utilizationCategory: "overhead",
    });
    const otherEventCategory = await serverAPI.createOtherEventCategory({
      drivingSchoolId: meta.drivingSchoolId,
      payload,
    });
    return { data: _toRecord(otherEventCategory) };
  }

  async update(
    _resource: string,
    { id, data }: UpdateParams<DrivingSchoolOtherEventCategory>,
  ): Promise<UpdateResult<DrivingSchoolOtherEventCategory>> {
    const [drivingSchoolId, otherEventCategoryId] = splitCompositeId(id);
    if (!drivingSchoolId) {
      throw new Error(`Invalid id: ${id} -- expected: "drivingSchoolId"`);
    }
    const otherEventCategory = await serverAPI.updateOtherEventCategory({
      id: otherEventCategoryId,
      drivingSchoolId,
      payload: { ...data, isWorkTime: data.isPaid ?? false },
    });
    return { data: _toRecord(otherEventCategory) };
  }

  async delete(_resource: string, { id }: DeleteParams<DrivingSchoolOtherEventCategory>): Promise<DeleteResult> {
    const [drivingSchoolId, otherEventCategoryId] = splitCompositeId(id);
    await serverAPI.deleteOtherEventCategory({ id: otherEventCategoryId, drivingSchoolId });
    return { data: {} };
  }
}

export const drivingSchoolOtherEventCategoriesProvider = new DrivingSchoolOtherEventCategoriesProvider();

function _toRecord(dto: OtherEventCategoryDto): DrivingSchoolOtherEventCategory {
  return {
    ...dto,
    id: `${dto.drivingSchoolId}:${dto.id}`,
  };
}
